import React, { useState } from "react";
import { backendApi } from "./api/backendApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Download, Star, TrendingUp, Clock, Award } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import PerformanceForm from "../components/performance/PerformanceForm";
import DetailModal from "./DetailModal";

const T = {
  green: '#1A3C2B', greenDeep: '#0E2118', greenTint: '#EDF3EF', greenGlow: 'rgba(26,60,43,0.1)',
  bg: '#F4F5F0', card: '#FFFFFF', line: '#E3E2DC', lineSoft: '#EEEEE9',
  ink: '#111418', body: '#3A4149', muted: '#687079', faint: '#9BA3AB',
};
const RATING_C = '#E0A33E';
const RATING_TINT = '#FBF1E1';

const StarRating = ({ rating, size = 14, interactive = false, onRate }) => (
  <div style={{ display: 'flex', gap: 2 }}>
    {[1,2,3,4,5].map(s => (
      <Star key={s} size={size} strokeWidth={1.8}
        onClick={() => interactive && onRate?.(s)}
        style={{
          color: s <= rating ? RATING_C : T.line,
          fill: s <= rating ? RATING_C : 'transparent',
          cursor: interactive ? 'pointer' : 'default',
          transition: 'color 0.1s, fill 0.1s',
        }} />
    ))}
  </div>
);

const MetricCard = ({ icon: Icon, value, label, color, tint, badge }) => (
  <div style={{ background:T.card, border:`1.5px solid ${T.line}`, borderRadius:18, padding:'20px 22px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', position:'relative', overflow:'hidden' }}>
    <div style={{ position:'absolute', top:0, right:0, width:80, height:80, borderRadius:'0 18px 0 80px', background:tint, opacity:0.5 }} />
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <div style={{ width:40, height:40, borderRadius:11, background:tint, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <Icon size={18} color={color} strokeWidth={2} />
      </div>
      <span style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:20, background:tint, color:color }}>{badge}</span>
    </div>
    <p style={{ fontSize:30, fontWeight:800, color:T.ink, margin:0, letterSpacing:'-0.03em' }}>{value}</p>
    <p style={{ fontSize:12, color:T.muted, margin:'4px 0 0', fontWeight:500 }}>{label}</p>
  </div>
);

const StatusPill = ({ status }) => {
  const done = String(status||'').toLowerCase().includes('complet');
  return (
    <span style={{ padding:'4px 10px', borderRadius:6, fontSize:11.5, fontWeight:600, background:done?'#E8F3EC':'#FBF1E1', color:done?'#2F7D4F':'#B9822E' }}>{status}</span>
  );
};

// Visual rating bar used in the employee card


export default function Performance() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [viewing, setViewing] = useState(null);
  const queryClient = useQueryClient();

  const { data: performanceData = [] } = useQuery({ queryKey:['performance'], queryFn:() => backendApi.entities.Performance.list(), initialData:[] });
  const { data: employees = [] } = useQuery({ queryKey:['employees'], queryFn:() => backendApi.entities.Employee.list(), initialData:[] });

  const createMutation = useMutation({
    mutationFn: (d) => backendApi.entities.Performance.create(d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey:['performance'] }); setShowAddForm(false); toast.success("Performance review added!"); },
  });

  const handleExport = () => {
    if (!performanceData.length) return toast.error("No performance data to export");
    const csv = [["Employee","Review Period","Rating","Strengths","Areas for Improvement"].join(","), ...performanceData.map(p => [p.employee_name,p.review_period,p.overall_rating||0,p.strengths||'',p.areas_for_improvement||''].join(","))].join("\n");
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download=`performance_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success("Exported successfully!");
  };

  const numericRatings = performanceData.map(p => Number(p.overall_rating)).filter(n => Number.isFinite(n) && n > 0);
  const avgRating = numericRatings.length > 0 ? (numericRatings.reduce((s,n)=>s+n,0)/numericRatings.length).toFixed(1) : '0.0';
  const reviewedIds = new Set(performanceData.map(p => p.employee_id));
  const totalEmp = employees.length;
  const pending = Math.max(0, totalEmp - reviewedIds.size);
  const completionPct = totalEmp > 0 ? Math.round((reviewedIds.size/totalEmp)*100) : 0;

  const reviewRows = [
    ...performanceData.map(perf => ({ perf, employee: employees.find(e => e.id === perf.employee_id) })),
    ...employees.filter(e => !reviewedIds.has(e.id)).map(employee => ({ perf: null, employee })),
  ];

  // Distribution for the rating histogram
  const ratingDist = [1,2,3,4,5].map(r => ({
    r,
    count: numericRatings.filter(n => Math.round(n) === r).length,
  }));
  const distMax = Math.max(1, ...ratingDist.map(d => d.count));

  return (
    <div style={{ background:T.bg, minHeight:'100%', padding:28 }}>
      <style>{`
        .perf-row:hover{background:#F7F8F5!important}
        .perf-btn{transition:all 0.15s;cursor:pointer}
        .perf-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,0,0,0.12)}
      `}</style>

      {/* Header */}
      <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'space-between', gap:16, marginBottom:26 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:800, color:T.ink, margin:0, letterSpacing:'-0.02em' }}>Performance Management</h2>
          <p style={{ fontSize:12.5, color:T.muted, margin:'3px 0 0' }}>Track reviews, ratings and employee growth</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="perf-btn" onClick={handleExport}
            style={{ display:'flex', alignItems:'center', gap:7, height:38, padding:'0 16px', background:T.card, border:`1.5px solid ${T.line}`, borderRadius:10, fontSize:13, fontWeight:600, color:T.body }}>
            <Download size={14} /> Export
          </button>
          <button className="perf-btn" onClick={() => setShowAddForm(true)}
            style={{ display:'flex', alignItems:'center', gap:7, height:38, padding:'0 18px', background:T.green, border:'none', borderRadius:10, fontSize:13, fontWeight:600, color:'#fff', boxShadow:'0 2px 10px rgba(26,60,43,0.2)' }}>
            <Plus size={14} strokeWidth={2.5} /> New Appraisal
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showAddForm && (
          <motion.div initial={{ opacity:0, y:-10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }}>
            <PerformanceForm employees={employees} onSubmit={d => createMutation.mutate(d)} onCancel={() => setShowAddForm(false)} isLoading={createMutation.isLoading} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Metric cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(155px,1fr))', gap:16, marginBottom:24 }}>
        <MetricCard icon={Star} value={avgRating} label="Avg. Rating" color={RATING_C} tint={RATING_TINT} badge="/ 5.0" />
        <MetricCard icon={Award} value={performanceData.length} label="Reviews Completed" color="#2F7D4F" tint="#E8F3EC" badge="Done" />
        <MetricCard icon={Clock} value={pending} label="Pending Reviews" color="#B9822E" tint="#FBF1E1" badge="Awaiting" />
        <MetricCard icon={TrendingUp} value={`${completionPct}%`} label="Review Coverage" color="#3B6FA6" tint="#EAF1FB" badge="Overall" />
      </div>

      {/* Rating distribution + completion */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 260px', gap:18, marginBottom:22 }}>
        {/* Histogram */}
        <div style={{ background:T.card, border:`1.5px solid ${T.line}`, borderRadius:18, padding:'20px 22px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
          <p style={{ fontSize:14.5, fontWeight:700, color:T.ink, margin:'0 0 18px' }}>Rating Distribution</p>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {ratingDist.reverse().map(({ r, count }) => (
              <div key={r} style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ display:'flex', gap:2, width:76, flexShrink:0 }}>
                  <StarRating rating={r} size={12} />
                </div>
                <div style={{ flex:1, height:8, background:T.lineSoft, borderRadius:99, overflow:'hidden' }}>
                  <div style={{ width:`${distMax>0?(count/distMax)*100:0}%`, height:'100%', background:RATING_C, borderRadius:99, transition:'width 0.8s cubic-bezier(0.16,1,0.3,1)' }} />
                </div>
                <span style={{ fontSize:12, fontWeight:600, color:T.ink, width:20, textAlign:'right' }}>{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Completion ring */}
        <div style={{ background:T.card, border:`1.5px solid ${T.line}`, borderRadius:18, padding:'20px 22px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
          <p style={{ fontSize:14.5, fontWeight:700, color:T.ink, margin:0, alignSelf:'flex-start' }}>Completion</p>
          <div style={{ position:'relative', width:120, height:120 }}>
            <svg viewBox="0 0 100 100" style={{ width:'100%', height:'100%', transform:'rotate(-90deg)' }}>
              <circle cx="50" cy="50" r="42" fill="transparent" stroke={T.lineSoft} strokeWidth="12" />
              <circle cx="50" cy="50" r="42" fill="transparent"
                stroke={T.green} strokeWidth="12" strokeLinecap="round"
                strokeDasharray={`${(completionPct/100)*2*Math.PI*42} ${2*Math.PI*42}`}
                style={{ transition:'stroke-dasharray 1s cubic-bezier(0.16,1,0.3,1)' }} />
            </svg>
            <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize:22, fontWeight:800, color:T.ink }}>{completionPct}%</span>
              <span style={{ fontSize:10, color:T.muted }}>complete</span>
            </div>
          </div>
          <div style={{ width:'100%' }}>
            {[{ label:'Reviewed', value:reviewedIds.size, color:T.green }, { label:'Pending', value:pending, color:'#B9822E' }].map(({ label, value, color }) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${T.lineSoft}` }}>
                <span style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, color:T.body }}>
                  <span style={{ width:8, height:8, borderRadius:2, background:color }} />{label}
                </span>
                <span style={{ fontSize:13, fontWeight:700, color:T.ink }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance table */}
      <div style={{ background:T.card, border:`1.5px solid ${T.line}`, borderRadius:18, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ padding:'16px 20px', borderBottom:`1.5px solid ${T.lineSoft}`, display:'flex', alignItems:'center', gap:8 }}>
          <Award size={16} color={T.green} strokeWidth={2} />
          <span style={{ fontSize:14.5, fontWeight:700, color:T.ink }}>Performance Reviews</span>
          <span style={{ marginLeft:'auto', fontSize:12, color:T.muted }}>{reviewRows.length} employees</span>
        </div>
        {reviewRows.length > 0 ? (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:T.lineSoft }}>
                  {['Employee','ID','Role','Department','Review Period','Rating','Status',''].map(h => (
                    <th key={h} style={{ padding:'11px 18px', textAlign:'left', fontSize:11, fontWeight:700, color:T.faint, textTransform:'uppercase', letterSpacing:'0.06em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reviewRows.map(({ perf, employee }, i) => {
                  const rating = Number(perf?.overall_rating) || 0;
                  const name = employee ? `${employee.first_name||''} ${employee.last_name||''}`.trim() : (perf?.employee_name || '—');
                  const initials = name.split(' ').map(p=>p[0]).join('').slice(0,2).toUpperCase();
                  return (
                    <tr key={perf?.id||employee?.id||i} className="perf-row" style={{ borderBottom:i<reviewRows.length-1?`1px solid ${T.lineSoft}`:'none', background:T.card }}>
                      <td style={{ padding:'13px 18px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:11 }}>
                          <div style={{ width:36, height:36, borderRadius:10, background:perf?T.greenTint:'#FBF1E1', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:perf?T.green:'#B9822E' }}>
                            {initials || '?'}
                          </div>
                          <span style={{ fontSize:13.5, fontWeight:600, color:T.ink }}>{name}</span>
                        </div>
                      </td>
                      <td style={{ padding:'13px 18px', fontSize:12.5, color:T.muted, fontFamily:'monospace' }}>{employee?.employee_id||'—'}</td>
                      <td style={{ padding:'13px 18px', fontSize:13, color:T.body }}>{employee?.position||'—'}</td>
                      <td style={{ padding:'13px 18px', fontSize:13, color:T.body }}>{employee?.department||'—'}</td>
                      <td style={{ padding:'13px 18px', fontSize:13, color:T.body }}>{perf?.review_period||'—'}</td>
                      <td style={{ padding:'13px 18px' }}>
                        {perf ? (
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <StarRating rating={rating} />
                            <span style={{ fontSize:11, color:T.muted }}>{rating}/5</span>
                          </div>
                        ) : <span style={{ color:T.faint, fontSize:13 }}>Not reviewed</span>}
                      </td>
                      <td style={{ padding:'13px 18px' }}><StatusPill status={perf ? "Completed" : "Pending"} /></td>
                      <td style={{ padding:'13px 18px' }}>
                        <button onClick={() => setViewing({ employee, perf })}
                          style={{ padding:'5px 12px', borderRadius:8, border:`1.5px solid ${T.line}`, background:T.card, fontSize:12, fontWeight:600, color:T.body, cursor:'pointer' }}>
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign:'center', padding:'60px 20px' }}>
            <Star size={40} color={T.faint} strokeWidth={1.5} style={{ display:'block', margin:'0 auto 12px' }} />
            <p style={{ fontSize:14, fontWeight:700, color:T.ink, margin:'0 0 4px' }}>No employees to review</p>
            <p style={{ fontSize:13, color:T.muted, margin:0 }}>Add employees first to start performance tracking.</p>
          </div>
        )}
      </div>

      <DetailModal open={!!viewing}
        title={viewing ? (`${viewing.employee?.first_name||''} ${viewing.employee?.last_name||''}`.trim() || viewing.perf?.employee_name || 'Employee') : ''}
        subtitle={viewing?.employee?.position}
        fields={[
          { label:"Employee ID", value:viewing?.employee?.employee_id },
          { label:"Department", value:viewing?.employee?.department },
          { label:"Review Period", value:viewing?.perf?.review_period },
          { label:"Overall Rating", value:viewing?.perf?.overall_rating },
          { label:"Goals Rating", value:viewing?.perf?.goals_rating },
          { label:"Skills Rating", value:viewing?.perf?.skills_rating },
          { label:"Strengths", value:viewing?.perf?.strengths },
          { label:"Areas for Improvement", value:viewing?.perf?.areas_for_improvement },
          { label:"Reviewer", value:viewing?.perf?.reviewer },
          { label:"Status", value:viewing?.perf ? (viewing.perf.status||"Completed") : "Pending" },
        ]}
        onClose={() => setViewing(null)} />
    </div>
  );
}